/**
 * Chats store bindings — runtime injection of cross-app stores into the
 * chats slice.
 *
 * Why: per FSD layering, `features/chats/` MUST NOT value-import from
 * `@/app/store/...`. Several chats internals legitimately need the UI store
 * and widget store at runtime (notably the zustand actions in
 * `chat.store.ts` which call `useUIStore.getState()` from inside async
 * callbacks where prop-injection is not viable).
 *
 * Pattern: an app-level bootstrap calls
 * `configureChatStores({ uiStore, widgetStore })` ONCE before any chats
 * code runs. Chats internals then call `getChatStores()` to obtain the
 * stores via the binding, never via direct import.
 *
 * The structural binding types below are defined locally so this file does
 * NOT import from `@/app/store/...` (the boundaries fs-walk regex would
 * flag even type-only imports). The page-side wiring code is the single
 * place where the concrete `useUIStore` / `useWidgetStore` references meet
 * the structural binding contract.
 */

// --- Structural binding types --------------------------------------------
//
// These mirror the narrow surface chats actually consumes. They MUST stay
// structurally compatible with the concrete `@/app/store/ui` and
// `@/app/store/widget` modules at the wiring site (`apps/agent-studio/
// src/main.tsx`). If those modules diverge, the wiring site's structural
// assignment will fail at compile time — that is the contract enforcement.

/**
 * Subset of `@/app/store/ui` consumed by chats. Intentionally narrow:
 * only the hooks + zustand-store handle that chats actually calls.
 */
export interface ChatsUiStoreBinding {
  /**
   * The zustand `useUIStore` hook. Chats calls
   * `getChatStores().uiStore.useUIStore.getState()` from inside zustand
   * action callbacks, and `useChatsUiStore(selector)` is allowed too — it
   * is the same stable reference returned by zustand's `create()`.
   */
  useUIStore: {
    getState: () => {
      sidePanelType: 'agents' | 'widget' | null;
      sidePanelLayoutMode: 'split' | 'fullscreen' | null;
      setSidePanelLayoutMode: (mode: 'split' | 'fullscreen' | null) => void;
      openSidePanel: (
        type: 'agents' | 'widget' | null,
        layoutMode?: 'split' | 'fullscreen' | null,
      ) => void;
      closeSidePanel: () => void;
    };
  };
  /** Derived hook: agent modal state + actions (uses `useShallow`). */
  useAgentModal: () => {
    isModalOpen: boolean;
    modalAgentId: string | null;
    onBeforeAgentSwitch: (() => void) | undefined;
    loading: boolean;
    error: string | null;
    openAgentModal: (agentId: string, onBeforeHandler?: () => void) => void;
    closeAgentModal: () => void;
  };
  /** Selector hook: which side panel is open. */
  useSidePanelType: () => 'agents' | 'widget' | null;
  /** Derived hook: side panel layout mode (uses `useShallow`). */
  useSidePanelLayoutMode: () => {
    sidePanelLayoutMode: 'split' | 'fullscreen' | null;
    setSidePanelLayoutMode: (mode: 'split' | 'fullscreen' | null) => void;
    lastNonNullLayoutMode: 'split' | 'fullscreen';
  };
}

/**
 * Subset of `@/app/store/widget` consumed by chats. The full surface is
 * wide; this binding exposes the zustand hook so chats can reach any state
 * + actions it needs (both reactively via selectors and non-reactively via
 * `.getState()`).
 *
 * Typed as `unknown`-bearing record so chats internals remain decoupled
 * from concrete widget-store shapes (the entities/widget envelope evolves
 * independently). Internal call sites cast at the boundary, which mirrors
 * the existing component-slot pattern.
 */
export interface ChatsWidgetStoreBinding {
  useWidgetStore: ChatsWidgetStoreHook;
}

/**
 * Bivariant call signature mirroring zustand's `UseBoundStore`:
 * - `useWidgetStore()` returns the full store
 * - `useWidgetStore(selector)` returns the selected slice
 * - `useWidgetStore.getState()` returns the full store synchronously
 *
 * Concrete shape is intentionally broad — internals supply selectors with
 * their own narrow expectations and cast at the boundary.
 */
export interface ChatsWidgetStoreHook {
  (): ChatsWidgetStoreState;
  <T>(selector: (state: ChatsWidgetStoreState) => T): T;
  getState: () => ChatsWidgetStoreState;
}

/**
 * Structural mirror of widget-version envelope (the chats slice consumes
 * this concrete shape via ChatWidgetToggle / ChatWidgetVersions). Intent:
 * keep chats decoupled from `@/app/store/widget/widget.store.types` while
 * preserving end-to-end type safety at the call sites.
 *
 * Mirrors `WidgetVersion` from `@/app/store/widget/widget.store.types`.
 * Wiring site asserts assignability.
 */
export interface ChatsWidgetVersion {
  id: string;
  version: number;
  messageId: string;
  agentId: string;
  widget: { key?: string; sourceAgentId?: string } & Record<string, unknown>;
  timestamp: number;
  title?: string;
}

/** Mirror of `AgentWidgetHistory`. */
export interface ChatsAgentWidgetHistory {
  agentId: string;
  versions: ChatsWidgetVersion[];
  activeVersionId?: string;
}

/**
 * Loose state shape — only the methods/properties chats actually touches
 * are documented here. Concrete typing happens at the wiring site (which
 * structurally assigns the real `WidgetStore` to `ChatsWidgetStoreState`).
 *
 * Index signature accommodates fields chats internals reach for via
 * selectors but doesn't need to enumerate (e.g. internal store actions).
 */
export interface ChatsWidgetStoreState {
  widget: ({ key?: string } & Record<string, unknown>) | null;
  agentHistories: Record<string, ChatsAgentWidgetHistory>;
  widgetMessages: Record<string, string[]>;
  setWidget: (widget: unknown) => void;
  clearWidgetPanel: () => void;
  hasWidgetForMessage: (messageId: string) => boolean;
  getActiveVersion: (agentId: string) => ChatsWidgetVersion | null;
  clearActiveVersion: (agentId: string) => void;
  switchToAgentVersion: (agentId: string, versionId: string) => void;
  setActiveVersion: (agentId: string, versionId: string) => void;
  getVersionsForMessage: (
    agentId: string,
    messageId: string,
    fallbackAgentId?: string,
  ) => { versions: ChatsWidgetVersion[]; resolvedAgentId: string };
  /**
   * Whether any mounted widget holds manual edits the user has not saved to an artifact. The composer
   * reads this before sending, because the agent works from the last SAVED artifact — an unsaved edit
   * would be silently discarded. Mirrors `WidgetActions.hasUnsavedWidgetEdits`.
   *
   * OPTIONAL on purpose: this is an advisory warning, not a capability the composer depends on. A
   * partial binding (a test stub, a host that wires a reduced store) must degrade to "just send",
   * never crash a send. Call it as `hasUnsavedWidgetEdits?.()`.
   */
  hasUnsavedWidgetEdits?: () => boolean;
  // Any other properties accessed by widget-version selectors.
  [key: string]: unknown;
}

/** Bag of stores chats consumes from `@/app/store/...`. */
export interface ChatStoreBindings {
  uiStore: ChatsUiStoreBinding;
  widgetStore: ChatsWidgetStoreBinding;
}

// --- Module-level binding state ------------------------------------------

let bindings: ChatStoreBindings | null = null;

/**
 * Wire concrete app stores into the chats slice. Call ONCE at app
 * bootstrap, BEFORE any chats code runs (e.g. before the React tree
 * mounts). Subsequent calls overwrite the binding — useful for tests.
 */
export function configureChatStores(next: ChatStoreBindings): void {
  bindings = next;
}

/**
 * Reset the binding to unconfigured state. Test-only escape hatch — call
 * from `afterEach` if a test mutated the binding via `configureChatStores`.
 */
export function resetChatStoreBindings(): void {
  bindings = null;
}

/**
 * Retrieve the bound stores. Throws a clear error if the bootstrap
 * forgot to call `configureChatStores` — fail-fast beats silent undefined
 * dereference inside a deeply-nested zustand action.
 */
export function getChatStores(): ChatStoreBindings {
  if (!bindings) {
    throw new Error(
      '[chats] Chat store bindings not configured. Call `configureChatStores({ uiStore, widgetStore })` at app bootstrap before any chats code runs (see features/chats/lib/chat-store-bindings.ts).',
    );
  }
  return bindings;
}

/**
 * Returns the current bindings or `null` (without throwing). For setup-time guards in test fixtures,
 * and for ADVISORY reads where an unbound store must not break the caller — e.g. the composer's
 * unsaved-widget-edits check, which exists to warn and must never block a send.
 */
export function peekChatStoreBindings(): ChatStoreBindings | null {
  return bindings;
}
